/**
 * 响应内容解析工具
 * 用于分离AI回复中的主内容和翻译内容
 */

// 翻译分离结果接口
export interface ParsedResponse {
  response: string;
  translation: string;
}

// 支持的翻译分隔符模式
const TRANSLATION_PATTERNS: (string | RegExp)[] = [
  // 常用分隔符
  "---TRANSLATION---",
  "**TRANSLATION:**",
  "**翻译:**",
  "**翻譯:**",
  "**TRADUCCIÓN:**",
  "**TRADUCTION:**",
  "**ÜBERSETZUNG:**",
  
  // 新增的Markdown标题格式
  "### **TRANSLATION**",
  "## **TRANSLATION**",
  "# **TRANSLATION**",
  
  // 正则表达式模式（不区分大小写）
  /\*\*TRANSLATION:\*\*/i,
  /\*\*翻译:\*\*/i,
  /\*\*翻譯:\*\*/i,
  /\*\*TRADUCCIÓN:\*\*/i,
  /\*\*TRADUCTION:\*\*/i,
  /\*\*ÜBERSETZUNG:\*\*/i,
  
  // Markdown标题格式的正则表达式
  /#{1,3}\s*\*\*TRANSLATION\*\*/i,
  /#{1,3}\s*\*\*翻译\*\*/i,
  /#{1,3}\s*\*\*翻譯\*\*/i,
];

/**
 * 分离回复内容中的主回复和翻译
 * @param content 完整的回复内容
 * @returns 分离后的主回复和翻译内容
 */
export function parseResponseContent(content: string): ParsedResponse {
  if (!content || typeof content !== 'string') {
    return { response: '', translation: '' };
  }

  const trimmedContent = content.trim();
  
  // 尝试各种翻译分隔符
  for (const pattern of TRANSLATION_PATTERNS) {
    if (typeof pattern === 'string') {
      // 字符串模式匹配
      if (trimmedContent.includes(pattern)) {
        const parts = trimmedContent.split(pattern);
        if (parts.length >= 2) {
          return {
            response: parts[0].trim(),
            translation: parts[1].trim()
          };
        }
      }
    } else {
      // 正则表达式模式匹配
      const match = trimmedContent.match(pattern);
      if (match && match.index !== undefined) {
        const splitIndex = match.index + match[0].length;
        return {
          response: trimmedContent.substring(0, match.index).trim(),
          translation: trimmedContent.substring(splitIndex).trim()
        };
      }
    }
  }
  
  // 如果没有找到分隔符，返回原始内容作为主回复
  return { response: trimmedContent, translation: '' };
}

/**
 * 检查内容是否包含翻译
 * @param content 回复内容
 * @returns 是否包含翻译
 */
export function hasTranslation(content: string): boolean {
  if (!content || typeof content !== 'string') {
    return false;
  }

  return TRANSLATION_PATTERNS.some(pattern => {
    if (typeof pattern === 'string') {
      return content.includes(pattern);
    } else {
      return pattern.test(content);
    }
  });
}

/**
 * 获取支持的翻译分隔符列表
 * @returns 翻译分隔符数组
 */
export function getSupportedTranslationPatterns(): readonly (string | RegExp)[] {
  return [...TRANSLATION_PATTERNS]; // 返回副本以保护原数组
}

/**
 * 添加自定义翻译分隔符
 * 注意：这个函数会修改全局模式列表，请谨慎使用
 * @param pattern 新的分隔符模式
 */
export function addTranslationPattern(pattern: string | RegExp): void {
  // 检查是否已存在
  const exists = TRANSLATION_PATTERNS.some(existing => {
    if (typeof existing === 'string' && typeof pattern === 'string') {
      return existing === pattern;
    } else if (existing instanceof RegExp && pattern instanceof RegExp) {
      return existing.source === pattern.source && existing.flags === pattern.flags;
    }
    return false;
  });

  if (!exists) {
    TRANSLATION_PATTERNS.push(pattern);
  }
}

/**
 * 验证翻译分离结果
 * @param parsed 分离结果
 * @returns 验证结果
 */
export function validateParsedResponse(parsed: ParsedResponse): {
  isValid: boolean;
  hasResponse: boolean;
  hasTranslation: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  
  if (!parsed) {
    errors.push('Parsed response is null or undefined');
    return { isValid: false, hasResponse: false, hasTranslation: false, errors };
  }

  const hasResponse = Boolean(parsed.response && parsed.response.trim());
  const hasTranslation = Boolean(parsed.translation && parsed.translation.trim());

  if (!hasResponse && !hasTranslation) {
    errors.push('Both response and translation are empty');
  }

  return {
    isValid: errors.length === 0,
    hasResponse,
    hasTranslation,
    errors
  };
} 